import { type ImportLookupKind } from '@utazzo/shared';
import { UzInput, UzSpinner } from '@utazzo/ui';
import { Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCitiesList } from '@/features/cities/use-cities';
import { useCountriesList } from '@/features/countries/use-countries';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { useDestinationsList } from '@/features/destinations/use-destinations';
import { useLanguagesList } from '@/features/languages/use-languages';
import { useMealPlansList } from '@/features/meal-plans/use-meal-plans';
import { useOccupancyTypesList } from '@/features/occupancy-types/use-occupancy-types';
import { useQueryMediumsList } from '@/features/query-mediums/use-query-mediums';
import { useQuerySourcesList } from '@/features/query-sources/use-query-sources';
import { useQueryTypesList } from '@/features/query-types/use-query-types';
import { useRoomTypesList } from '@/features/room-types/use-room-types';
import { useSuppliersList } from '@/features/suppliers/use-suppliers';
import { useUsersList } from '@/features/users/use-users';
import { useVehicleTypesList } from '@/features/vehicle-types/use-vehicle-types';

interface ImportLookupCellProps {
  /** Master kind to search. */
  kind: ImportLookupKind;
  /** Field's current value (typically the master's display name). */
  value: string;
  /** Imported value from the source XLSX - shown as a tooltip. */
  importedValue: string;
  /** Validation message attached to this cell, if any. */
  error?: string;
  /** Hint shown below the field when no error. */
  hint?: string;
  /** Fires the picked master row's name (not id - the importer
   *  resolves names → ids at validate time). When the operator picks
   *  from the dropdown, `pickedId` is also sent so duplicate names
   *  resolve unambiguously on re-validate. */
  onChange: (next: string, pickedId?: string) => void;
  /** Stable name for the DOM input. */
  name: string;
}

/// Min chars before we hit the server. Keeps an idle dropdown from
/// firing requests as the operator tabs through the row.
const MIN_CHARS = 2;
/// Cap each master query at 25 hits - the panel is a typeahead, not
/// a list view. Operators narrow with text, not by scrolling.
const PAGE_SIZE = 25;
/// Debounce window so each keystroke doesn't fire a request.
const DEBOUNCE_MS = 200;

/**
 * Per-cell async master search picker used by the lead-import row
 * editor when a column carries `type: 'lookup'`. Replaces the static
 * `<UzSelect options={[...58k rows]} />` we used to ship - this only
 * fetches results once the operator types ≥ 2 characters AND has the
 * cell focused, so an idle preview with 100 rows now costs zero
 * master queries instead of one bulk fetch per page.
 *
 * Save-on-pick writes back the master row's NAME (not id). The
 * importer's existing `validateRow` re-resolves the name → id via
 * the `loadResolvers` cache; nothing on the wire changes for
 * downstream commit.
 */
export const ImportLookupCell = ({
  kind,
  value,
  importedValue,
  error,
  hint,
  onChange,
  name,
}: ImportLookupCellProps): React.JSX.Element => {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync prop -> local draft when an outside caller (e.g. server
  // re-validate after save) replaces the value.
  useEffect(() => setDraft(value), [value]);

  const handlePick = (label: string, pickedId: string): void => {
    setDraft(label);
    onChange(label, pickedId);
    setFocused(false);
  };

  return (
    <div className="relative">
      <UzInput
        name={name}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => {
          if (blurTimer.current) clearTimeout(blurTimer.current);
          setFocused(true);
        }}
        onBlur={() => {
          // Commit free-text on blur so the operator's edit survives
          // even when they didn't pick from the dropdown. 150 ms guard
          // so a row-click can still fire before we close.
          blurTimer.current = setTimeout(() => {
            setFocused(false);
            if (draft !== value) onChange(draft);
          }, 150);
        }}
        type="text"
        autoComplete="off"
        leftAdornment={<Search className="h-3.5 w-3.5" />}
        error={error}
        placeholder={
          importedValue ? `Sheet: ${importedValue}` : draft ? draft : `Search ${KIND_LABEL[kind]}…`
        }
        title={importedValue ? `Imported from sheet: ${importedValue}` : undefined}
      />
      {/* The panel is the ONLY thing that mounts the master-search
          query - so 100 collapsed rows = 0 master fetches. The panel
          only renders when the cell is focused, and the inner hook
          waits for ≥ 2 chars before issuing the network call. */}
      {focused && <LookupPanel kind={kind} term={draft} onPick={handlePick} />}
      {hint && !error && <p className="mt-1 text-[11px] text-zinc-500">{hint}</p>}
    </div>
  );
};

// ---------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------

const KIND_LABEL: Record<ImportLookupKind, string> = {
  city: 'cities',
  destination: 'destinations',
  user: 'users',
  queryType: 'query types',
  querySource: 'sources',
  queryMedium: 'mediums',
  country: 'countries',
  currency: 'currencies',
  mealPlan: 'meal plans',
  roomType: 'room types',
  occupancyType: 'occupancy types',
  supplier: 'suppliers',
  language: 'languages',
  vehicleType: 'vehicle types',
};

interface LookupRow {
  id: string;
  label: string;
  subtitle?: string;
}

const LookupPanel = ({
  kind,
  term,
  onPick,
}: {
  kind: ImportLookupKind;
  term: string;
  onPick: (label: string, pickedId: string) => void;
}): React.JSX.Element => {
  // Debounce term -> query so each keystroke doesn't fire a request.
  const trimmed = term.trim();
  const [debounced, setDebounced] = useState(trimmed);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [trimmed]);

  const ready = debounced.length >= MIN_CHARS;
  const { rows, isFetching } = useLookupResults({ kind, term: debounced, ready });

  return (
    <div className="absolute top-full right-0 left-0 z-20 mt-1 max-h-56 overflow-y-auto rounded-md border border-zinc-200 bg-white shadow-lg">
      {!ready ? (
        <div className="p-2.5 text-xs text-zinc-500">Type ≥ {MIN_CHARS} characters to search.</div>
      ) : isFetching ? (
        <div className="flex items-center gap-2 p-2.5 text-xs text-zinc-500">
          <UzSpinner size="sm" /> Searching…
        </div>
      ) : rows.length === 0 ? (
        <div className="p-2.5 text-xs text-zinc-500">
          No matches. Edit the text or skip this row.
        </div>
      ) : (
        <ul className="divide-y divide-zinc-100">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onPick(row.label, row.id)}
                className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-zinc-50"
              >
                <span className="font-medium text-zinc-900">{row.label}</span>
                {row.subtitle && <span className="ml-1.5 text-zinc-500">· {row.subtitle}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Only ONE of the five master hooks is called with a meaningful query;
 * the rest skip the network because we gate the query params on the
 * kind. Each hook still mounts a React-Query subscription, but that's
 * cheap when no fetch fires.
 */
const useLookupResults = ({
  kind,
  term,
  ready,
}: {
  kind: ImportLookupKind;
  term: string;
  ready: boolean;
}): { rows: LookupRow[]; isFetching: boolean } => {
  const cityQuery = useCitiesList(
    ready && kind === 'city'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
          searchMode: 'prefix' as const,
        }
      : DISABLED_QUERY.city,
  );
  const userQuery = useUsersList(
    ready && kind === 'user'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'fullName',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.user,
  );
  const queryTypeQuery = useQueryTypesList(
    ready && kind === 'queryType'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.queryType,
  );
  const querySourceQuery = useQuerySourcesList(
    ready && kind === 'querySource'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.querySource,
  );
  const queryMediumQuery = useQueryMediumsList(
    ready && kind === 'queryMedium'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.queryMedium,
  );
  const countryQuery = useCountriesList(
    ready && kind === 'country'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.country,
  );
  const currencyQuery = useCurrenciesList(
    ready && kind === 'currency'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'code',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.currency,
  );
  const mealPlanQuery = useMealPlansList(
    ready && kind === 'mealPlan'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.mealPlan,
  );
  const roomTypeQuery = useRoomTypesList(
    ready && kind === 'roomType'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.roomType,
  );
  const occupancyTypeQuery = useOccupancyTypesList(
    ready && kind === 'occupancyType'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.occupancyType,
  );
  const supplierQuery = useSuppliersList(
    ready && kind === 'supplier'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.supplier,
  );
  const languageQuery = useLanguagesList(
    ready && kind === 'language'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.language,
  );
  const destinationQuery = useDestinationsList(
    ready && kind === 'destination'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.destination,
  );
  const vehicleTypeQuery = useVehicleTypesList(
    ready && kind === 'vehicleType'
      ? {
          page: 1,
          pageSize: PAGE_SIZE,
          sortBy: 'name',
          sortDir: 'asc',
          isActive: true,
          search: term,
        }
      : DISABLED_QUERY.vehicleType,
  );

  return useMemo(() => {
    if (kind === 'destination') {
      return {
        rows: (destinationQuery.data?.data ?? []).map((d) => ({
          id: d.id,
          label: d.name,
          subtitle: d.country?.name ?? undefined,
        })),
        isFetching: destinationQuery.isFetching,
      };
    }
    if (kind === 'city') {
      return {
        rows: (cityQuery.data?.data ?? []).map((c) => ({
          id: c.id,
          label: c.name,
          subtitle: [c.state?.name, c.country?.name].filter(Boolean).join(' · '),
        })),
        isFetching: cityQuery.isFetching,
      };
    }
    if (kind === 'user') {
      return {
        rows: (userQuery.data?.data ?? []).map((u) => ({ id: u.id, label: u.fullName })),
        isFetching: userQuery.isFetching,
      };
    }
    if (kind === 'queryType') {
      return {
        rows: (queryTypeQuery.data?.data ?? []).map((q) => ({ id: q.id, label: q.name })),
        isFetching: queryTypeQuery.isFetching,
      };
    }
    if (kind === 'querySource') {
      return {
        rows: (querySourceQuery.data?.data ?? []).map((q) => ({ id: q.id, label: q.name })),
        isFetching: querySourceQuery.isFetching,
      };
    }
    if (kind === 'queryMedium') {
      return {
        rows: (queryMediumQuery.data?.data ?? []).map((q) => ({ id: q.id, label: q.name })),
        isFetching: queryMediumQuery.isFetching,
      };
    }
    if (kind === 'country') {
      return {
        rows: (countryQuery.data?.data ?? []).map((c) => ({
          id: c.id,
          label: c.name,
          subtitle: c.isoCode ?? undefined,
        })),
        isFetching: countryQuery.isFetching,
      };
    }
    if (kind === 'currency') {
      return {
        rows: (currencyQuery.data?.data ?? []).map((c) => ({
          id: c.id,
          // For currencies the operator types the code (USD/INR) - that's
          // also the natural display.
          label: c.code,
          subtitle: c.name,
        })),
        isFetching: currencyQuery.isFetching,
      };
    }
    if (kind === 'mealPlan') {
      return {
        rows: (mealPlanQuery.data?.data ?? []).map((m) => ({
          id: m.id,
          label: m.name,
          subtitle: m.code ?? undefined,
        })),
        isFetching: mealPlanQuery.isFetching,
      };
    }
    if (kind === 'roomType') {
      return {
        rows: (roomTypeQuery.data?.data ?? []).map((r) => ({ id: r.id, label: r.name })),
        isFetching: roomTypeQuery.isFetching,
      };
    }
    if (kind === 'occupancyType') {
      return {
        rows: (occupancyTypeQuery.data?.data ?? []).map((o) => ({ id: o.id, label: o.name })),
        isFetching: occupancyTypeQuery.isFetching,
      };
    }
    if (kind === 'supplier') {
      return {
        rows: (supplierQuery.data?.data ?? []).map((s) => ({ id: s.id, label: s.name })),
        isFetching: supplierQuery.isFetching,
      };
    }
    if (kind === 'language') {
      return {
        rows: (languageQuery.data?.data ?? []).map((l) => ({ id: l.id, label: l.name })),
        isFetching: languageQuery.isFetching,
      };
    }
    if (kind === 'vehicleType') {
      return {
        rows: (vehicleTypeQuery.data?.data ?? []).map((v) => ({ id: v.id, label: v.name })),
        isFetching: vehicleTypeQuery.isFetching,
      };
    }
    return { rows: [], isFetching: false };
  }, [
    kind,
    cityQuery,
    userQuery,
    queryTypeQuery,
    querySourceQuery,
    queryMediumQuery,
    countryQuery,
    currencyQuery,
    mealPlanQuery,
    roomTypeQuery,
    occupancyTypeQuery,
    supplierQuery,
    languageQuery,
    destinationQuery,
    vehicleTypeQuery,
  ]);
};

/// Sentinel query objects passed to the "not this kind" hooks. They
/// produce a stable query key that React-Query treats as cacheable
/// no-op (pageSize: 1, search: '__noop__' so it'd never match
/// anything if it did fire - and the hook is gated on `enabled` via
/// the query-key change pattern: when kind doesn't match, the query
/// arguments are identical for every call, so React-Query reuses
/// whatever's in cache and doesn't refetch).
const DISABLED_QUERY = {
  city: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  user: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'fullName',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  queryType: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  querySource: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  queryMedium: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  country: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  currency: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'code',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  mealPlan: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  roomType: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  occupancyType: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  supplier: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  language: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  destination: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
  vehicleType: {
    page: 1 as const,
    pageSize: 1 as const,
    sortBy: 'name',
    sortDir: 'asc' as const,
    isActive: true,
    search: '__noop__',
  },
} as const;
